import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../apiBase';
import CountUp from '../components/CountUp';

const COST = 100;
const MAX_LENGTH = 200;

export default function TTS() {
  const { user, login, refresh } = useAuth();
  const [message, setMessage] = useState('');
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState('');
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  async function loadRecent() {
    const res = await apiFetch('/api/tts/recent');
    setRecent(await res.json());
  }

  useEffect(() => {
    loadRecent();
    apiFetch('/api/tts/voices')
      .then((res) => res.json())
      .then((list) => {
        setVoices(list);
        if (list.length) setVoice(list[0].key);
      });
  }, []);

  async function handleSend() {
    if (!message.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch('/api/tts/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, voice }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Send failed');
      setToast("Sent! It'll play on stream shortly.");
      setMessage('');
      await Promise.all([loadRecent(), refresh()]);
      setTimeout(() => setToast(''), 4000);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const notEnoughPoints = user && user.points < COST;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <h1 className="font-display-lg uppercase text-3xl">Text-to-Speech</h1>
          <p className="text-sm text-[#888899] mt-1">Pay points, get your message read out loud on stream.</p>
        </div>
        {user && (
          <div className="rounded-xl px-4 py-2 bg-[#12121a] border border-[#F6BB4248] flex items-center gap-2">
            <span className="font-mono-brand text-[10px] text-[#7d7d8c] uppercase tracking-widest">Balance</span>
            <span className="font-mono-brand text-lg font-black text-[#F6BB42]"><CountUp value={user.points} /></span>
          </div>
        )}
      </div>

      {toast && (
        <div className="mb-6 rounded-lg px-4 py-3 bg-[#00E701]/10 border border-[#00E701]/40 text-[#00E701] text-sm font-bold">
          {toast}
        </div>
      )}

      <div className="bg-[#0e0e15] border border-[#23232e] rounded-xl p-6 mb-10">
        <label className="font-mono-brand text-[10px] text-[#7d7d8c] uppercase tracking-widest block mb-2">Voice</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {voices.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setVoice(v.key)}
              className={`rounded-lg py-2 text-xs font-bold border transition-colors ${
                voice === v.key
                  ? 'border-[#F6BB42] bg-[#F6BB42]/10 text-[#F6BB42]'
                  : 'border-[#23232e] bg-[#0a0a10] text-[#888899] hover:text-white'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
          placeholder="Type what you want the bot to say..."
          rows={3}
          className="w-full bg-[#0a0a10] border border-[#23232e] rounded-lg p-3 text-sm text-white resize-none focus:border-[#F6BB42]/50"
        />
        <div className="flex items-center justify-between mt-3">
          <span className="font-mono-brand text-[11px] text-[#7d7d8c]">{message.length}/{MAX_LENGTH}</span>
          <span className="font-mono-brand text-sm font-black text-[#F6BB42]">🪙 {COST}</span>
        </div>
        {error && <p className="text-sm text-[#ff544a] mt-3">{error}</p>}
        <button
          disabled={!message.trim() || busy || notEnoughPoints}
          onClick={() => (user ? handleSend() : login())}
          className="btn-primary shine-hover rounded-lg py-2.5 text-xs w-full mt-4 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {!user
            ? 'Sign in to send'
            : notEnoughPoints
              ? 'Not enough points'
              : busy
                ? 'Sending…'
                : `Send for 🪙 ${COST}`}
        </button>
      </div>

      <h2 className="font-display-lg uppercase text-lg mb-4">Recently Sent</h2>
      {!recent.length ? (
        <div className="text-center text-[#888899] py-12">No messages yet — be the first.</div>
      ) : (
        <div className="flex flex-col gap-2">
          {recent.map((r, i) => (
            <div
              key={r.id}
              className="scroll-reveal bg-[#0e0e15] border border-[#23232e] rounded-lg px-4 py-3 flex items-center justify-between gap-4"
              style={{ transitionDelay: `${Math.min(i, 9) * 60}ms` }}
            >
              <div className="min-w-0">
                <div className="text-xs font-bold text-white">
                  {r.username}
                  {r.voice_label && <span className="text-[#F6BB42] font-normal"> · {r.voice_label}</span>}
                </div>
                <div className="text-sm text-[#c9c9d6] truncate">{r.message}</div>
              </div>
              <span className="font-mono-brand text-[10px] text-[#7d7d8c] flex-shrink-0">🪙 <CountUp value={r.price_paid} /></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
